import { Bank } from "@/interfaces/Bank";
import { CustomAxiosRequestConfig } from "@/interfaces/customAxiosRequestConfig";
import axios from "@/lib/axios";
import { useMutation, useQuery } from "@tanstack/vue-query";

interface Props {
  immediate?: boolean;
  erroHandling?: boolean;
}

const getBanks = async (errorHandling = true): Promise<Bank[]> => {
  const { data } = await axios.get<Bank[]>("/api/internal/banks", {
    errorHandling,
  } as CustomAxiosRequestConfig);

  return data;
};

const createBank = async (params: { name: string }) => {
  const { data } = await axios.post("/api/internal/banks", params);

  return data;
};

const updateBank = async (params: { name: string; id: string | number }) => {
  const { data } = await axios.put(
    `/api/internal/banks/${params.id}`,
    params.name,
  );

  return data;
};

const deleteBank = async (id: string | number) => {
  const { data } = await axios.delete(`/api/internal/banks/${id}`);

  return data;
};

const restoreBank = async (id: string | number) => {
  const { data } = await axios.put(`/api/internal/banks/${id}/restore`);

  return data;
};

const useBanks = (props: Props) => {
  const { immediate = true, erroHandling = true } = props;

  const { data, isLoading, refetch } = useQuery(
    ["banks"],
    () => getBanks(erroHandling),
    {
      enabled: immediate,
    },
  );

  const createBankMutation = useMutation((params: { name: string }) =>
    createBank(params),
  );

  const updateBankMutation = useMutation(
    (params: { name: string; id: string }) => updateBank(params),
  );

  const deleteBankMutation = useMutation((id: string) => deleteBank(id));

  const restoreBankMutation = useMutation((id: string) => restoreBank(id));

  return {
    data,
    isLoading,
    refetch,
    createBankMutation,
    updateBankMutation,
    deleteBankMutation,
    restoreBankMutation,
  };
};

export default useBanks;
